import { SalonInvoiceProfile } from '../../services/peInvoiceFirebaseService';
import {
  InvoiceCustomer,
  InvoiceItem,
  InvoiceShipping,
  InvoiceMeta,
  InvoiceCalculations,
} from '../../store/usePEInvoiceStore';

export interface ZugferdParams {
  profile: SalonInvoiceProfile;
  customer: InvoiceCustomer;
  items: InvoiceItem[];
  shipping: InvoiceShipping;
  meta: InvoiceMeta;
  calculations: InvoiceCalculations;
}

function escapeXml(unsafe: string): string {
  if (!unsafe) return '';
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case "'":
        return '&apos;';
      case '"':
        return '&quot;';
      default:
        return c;
    }
  });
}

function mapUnitToUnece(unit: string): string {
  const u = (unit || '').toLowerCase().trim();
  if (u.includes('min')) return 'MIN';
  if (u.includes('std') || u.includes('stunde')) return 'HUR';
  if (u.includes('pauschal')) return 'LS';
  if (u === 'm' || u.includes('meter')) return 'MTR';
  if (u.includes('kg')) return 'KGM';
  return 'C62';
}

function formatDate(dateStr: string): string {
  return (dateStr || '').replace(/-/g, '');
}

function getLineNetAmount(
  item: InvoiceItem,
  effectiveRate: number
): { lineNet: number; unitNet: number } {
  const lineTotalGrossOrNet = (item.quantity || 0) * (item.unitPrice || 0);
  if (item.priceMode === 'gross' && effectiveRate > 0) {
    const lineNet = lineTotalGrossOrNet / (1 + effectiveRate / 100);
    const unitNet = (item.unitPrice || 0) / (1 + effectiveRate / 100);
    return { lineNet, unitNet };
  }
  return { lineNet: lineTotalGrossOrNet, unitNet: item.unitPrice || 0 };
}

/** Steuerbefreiungsgrund für CategoryCode E (§ 19 / § 13b) — EN16931 verlangt Begründung. */
function resolveVatExemptionReason(metaNotes: string | undefined): string {
  const notes = String(metaNotes || '').trim();
  if (/13b/i.test(notes)) {
    return notes || 'Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG).';
  }
  if (/19\s*UStG|Kleinunternehmer/i.test(notes)) {
    return notes;
  }
  return 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.';
}

export function generateZugferdXml(params: ZugferdParams): string {
  const { profile, customer, items, shipping, meta, calculations } = params;
  const isVatExempt = calculations.isVatExempt;
  const standardVatRate = calculations.standardVatRate || 0;
  const issueDateFormatted = formatDate(meta.invoiceDate);
  const deliveryDateFormatted = formatDate(meta.deliveryDate);

  const sellerCompanyName = String(
    profile.senderCompanyName || profile.accountHolder || 'Rechnungssteller'
  ).trim();
  const sellerPersonName = String(
    profile.accountHolder || profile.senderCompanyName || 'Kontakt'
  ).trim();
  // DATEV-Kreditor-Match: oft Personenname (wie bisherige Zahlungen), nicht nur Salonfirma
  const sellerTradePartyName =
    sellerPersonName &&
    sellerCompanyName &&
    sellerPersonName.toLowerCase() !== sellerCompanyName.toLowerCase()
      ? `${sellerPersonName} - ${sellerCompanyName}`
      : sellerCompanyName || sellerPersonName;
  const exemptionReason = isVatExempt ? resolveVatExemptionReason(meta.notes) : '';
  const exemptionReasonXml = isVatExempt
    ? `<ram:ExemptionReason>${escapeXml(exemptionReason)}</ram:ExemptionReason>`
    : '';

  const cleanIban = (profile.iban || '').replace(/\s+/g, '').toUpperCase();
  const cleanBic = (profile.bic || '').replace(/\s+/g, '').toUpperCase();
  const paymentDays = Number(meta.paymentDeadlineDays) > 0 ? Number(meta.paymentDeadlineDays) : 14;
  let dueDateFormatted = issueDateFormatted;
  try {
    const due = new Date(`${meta.invoiceDate || ''}T12:00:00`);
    if (!Number.isNaN(due.getTime())) {
      due.setDate(due.getDate() + paymentDays);
      dueDateFormatted = formatDate(due.toISOString().slice(0, 10));
    }
  } catch {
    // issueDate bleibt
  }

  const paymentMeansXml = cleanIban
    ? `
      <ram:SpecifiedTradeSettlementPaymentMeans>
        <ram:TypeCode>58</ram:TypeCode>
        <ram:Information>SEPA-Überweisung</ram:Information>
        <ram:PayeePartyCreditorFinancialAccount>
          <ram:IBANID>${cleanIban}</ram:IBANID>
          <ram:AccountName>${escapeXml(sellerPersonName)}</ram:AccountName>
        </ram:PayeePartyCreditorFinancialAccount>
        ${cleanBic ? `<ram:PayeeSpecifiedCreditorFinancialInstitution><ram:BICID>${cleanBic}</ram:BICID></ram:PayeeSpecifiedCreditorFinancialInstitution>` : ''}
      </ram:SpecifiedTradeSettlementPaymentMeans>`
    : '';

  let itemXml = '';
  let linesNetSum = 0;

  items.forEach((item, index) => {
    const effectiveRate = isVatExempt ? 0 : standardVatRate;
    const { lineNet, unitNet } = getLineNetAmount(item, effectiveRate);
    linesNetSum += lineNet;

    const vatCategory = isVatExempt ? 'E' : effectiveRate === 0 ? 'Z' : 'S';
    const unitCode = mapUnitToUnece(item.unit);

    itemXml += `
    <ram:IncludedSupplyChainTradeLineItem>
      <ram:AssociatedDocumentLineDocument>
        <ram:LineID>${index + 1}</ram:LineID>
      </ram:AssociatedDocumentLineDocument>
      <ram:SpecifiedTradeProduct>
        <ram:Name>${escapeXml(item.description || 'Position ' + (index + 1))}</ram:Name>
      </ram:SpecifiedTradeProduct>
      <ram:SpecifiedLineTradeAgreement>
        <ram:NetPriceProductTradePrice>
          <ram:ChargeAmount>${unitNet.toFixed(2)}</ram:ChargeAmount>
        </ram:NetPriceProductTradePrice>
      </ram:SpecifiedLineTradeAgreement>
      <ram:SpecifiedLineTradeDelivery>
        <ram:BilledQuantity unitCode="${unitCode}">${item.quantity}</ram:BilledQuantity>
      </ram:SpecifiedLineTradeDelivery>
      <ram:SpecifiedLineTradeSettlement>
        <ram:ApplicableTradeTax>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:CategoryCode>${vatCategory}</ram:CategoryCode>
          <ram:RateApplicablePercent>${effectiveRate}</ram:RateApplicablePercent>
          ${isVatExempt ? exemptionReasonXml : ''}
        </ram:ApplicableTradeTax>
        <ram:SpecifiedTradeSettlementLineMonetarySummation>
          <ram:LineTotalAmount>${lineNet.toFixed(2)}</ram:LineTotalAmount>
        </ram:SpecifiedTradeSettlementLineMonetarySummation>
      </ram:SpecifiedLineTradeSettlement>
    </ram:IncludedSupplyChainTradeLineItem>`;
  });

  const shippingNet =
    shipping.enabled && shipping.amount > 0 ? Number(shipping.amount) || 0 : 0;
  const shippingXml =
    shippingNet > 0
      ? `
      <ram:SpecifiedTradeAllowanceCharge>
        <ram:ChargeIndicator>
          <udt:Indicator>true</udt:Indicator>
        </ram:ChargeIndicator>
        <ram:ActualAmount>${shippingNet.toFixed(2)}</ram:ActualAmount>
        <ram:Reason>Versand- &amp; Verpackungskosten</ram:Reason>
        <ram:CategoryTradeTax>
          <ram:TypeCode>VAT</ram:TypeCode>
          <ram:CategoryCode>${isVatExempt ? 'E' : 'S'}</ram:CategoryCode>
          <ram:RateApplicablePercent>${isVatExempt ? 0 : standardVatRate}</ram:RateApplicablePercent>
          ${isVatExempt ? exemptionReasonXml : ''}
        </ram:CategoryTradeTax>
      </ram:SpecifiedTradeAllowanceCharge>`
      : '';

  const buyerName = escapeXml(
    customer.companyName || `${customer.firstName} ${customer.lastName}`.trim() || 'Empfänger'
  );

  return `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100"
  xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100">
  <rsm:ExchangedDocumentContext>
    <ram:GuidelineSpecifiedDocumentContextParameter>
      <ram:ID>urn:cen.eu:en16931:2017#compliant#urn:factur-x.eu:1p0:en16931</ram:ID>
    </ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>
  <rsm:ExchangedDocument>
    <ram:ID>${escapeXml(meta.invoiceNumber)}</ram:ID>
    <ram:TypeCode>380</ram:TypeCode>
    <ram:IssueDateTime>
      <udt:DateTimeString format="102">${issueDateFormatted}</udt:DateTimeString>
    </ram:IssueDateTime>
    ${meta.notes ? `<ram:IncludedNote><ram:Content>${escapeXml(meta.notes)}</ram:Content></ram:IncludedNote>` : ''}
  </rsm:ExchangedDocument>
  <rsm:SupplyChainTradeTransaction>
    ${itemXml}
    <ram:ApplicableHeaderTradeAgreement>
      ${customer.buyerReference ? `<ram:BuyerReference>${escapeXml(customer.buyerReference)}</ram:BuyerReference>` : ''}
      <ram:SellerTradeParty>
        <ram:Name>${escapeXml(sellerTradePartyName)}</ram:Name>
        <ram:DefinedTradeContact>
          <ram:PersonName>${escapeXml(sellerPersonName)}</ram:PersonName>${profile.senderEmail ? `<ram:EmailURIUniversalCommunication><ram:URIID>${escapeXml(profile.senderEmail)}</ram:URIID></ram:EmailURIUniversalCommunication>` : ''}
          ${profile.senderPhone ? `<ram:TelephoneUniversalCommunication><ram:CompleteNumber>${escapeXml(profile.senderPhone)}</ram:CompleteNumber></ram:TelephoneUniversalCommunication>` : ''}
        </ram:DefinedTradeContact>
        <ram:PostalTradeAddress>
          <ram:PostcodeCode>${escapeXml(profile.senderZip || '')}</ram:PostcodeCode>
          <ram:LineOne>${escapeXml(profile.senderStreet || '')}</ram:LineOne>
          <ram:CityName>${escapeXml(profile.senderCity || '')}</ram:CityName>
          <ram:CountryID>${escapeXml(profile.senderCountry || 'DE')}</ram:CountryID>
        </ram:PostalTradeAddress>
        ${profile.vatId ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${escapeXml(profile.vatId)}</ram:ID></ram:SpecifiedTaxRegistration>` : ''}
        ${profile.taxNumber ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="FC">${escapeXml(profile.taxNumber)}</ram:ID></ram:SpecifiedTaxRegistration>` : ''}
      </ram:SellerTradeParty>
      <ram:BuyerTradeParty>
        <ram:Name>${buyerName}</ram:Name>
        ${customer.contactPerson ? `<ram:DefinedTradeContact><ram:PersonName>${escapeXml(customer.contactPerson)}</ram:PersonName></ram:DefinedTradeContact>` : ''}
        <ram:PostalTradeAddress>
          <ram:PostcodeCode>${escapeXml(customer.zip || '')}</ram:PostcodeCode>
          <ram:LineOne>${escapeXml(customer.street || '')}</ram:LineOne>
          <ram:CityName>${escapeXml(customer.city || '')}</ram:CityName>
          <ram:CountryID>${escapeXml(customer.country || 'DE')}</ram:CountryID>
        </ram:PostalTradeAddress>
        ${customer.vatId ? `<ram:SpecifiedTaxRegistration><ram:ID schemeID="VA">${escapeXml(customer.vatId)}</ram:ID></ram:SpecifiedTaxRegistration>` : ''}
      </ram:BuyerTradeParty>
    </ram:ApplicableHeaderTradeAgreement>
    <ram:ApplicableHeaderTradeDelivery>
      <ram:ActualDeliverySupplyChainEvent>
        <ram:OccurrenceDateTime>
          <udt:DateTimeString format="102">${deliveryDateFormatted}</udt:DateTimeString>
        </ram:OccurrenceDateTime>
      </ram:ActualDeliverySupplyChainEvent>
    </ram:ApplicableHeaderTradeDelivery>
    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>EUR</ram:InvoiceCurrencyCode>
      ${paymentMeansXml}
      <ram:ApplicableTradeTax>
        <ram:CalculatedAmount>${calculations.vatTotal.toFixed(2)}</ram:CalculatedAmount>
        <ram:TypeCode>VAT</ram:TypeCode>
        <ram:BasisAmount>${calculations.netTotal.toFixed(2)}</ram:BasisAmount>
        <ram:CategoryCode>${isVatExempt ? 'E' : 'S'}</ram:CategoryCode>
        <ram:RateApplicablePercent>${isVatExempt ? 0 : standardVatRate}</ram:RateApplicablePercent>
        ${isVatExempt ? exemptionReasonXml : ''}
      </ram:ApplicableTradeTax>
      ${shippingXml}
      <ram:SpecifiedTradePaymentTerms>
        <ram:Description>Zahlbar innerhalb von ${paymentDays} Tagen ohne Abzug.</ram:Description>
        <ram:DueDateDateTime>
          <udt:DateTimeString format="102">${dueDateFormatted}</udt:DateTimeString>
        </ram:DueDateDateTime>
      </ram:SpecifiedTradePaymentTerms>
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:LineTotalAmount>${linesNetSum.toFixed(2)}</ram:LineTotalAmount>
        <ram:ChargeTotalAmount>${shippingNet.toFixed(2)}</ram:ChargeTotalAmount>
        <ram:TaxBasisTotalAmount>${calculations.netTotal.toFixed(2)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="EUR">${calculations.vatTotal.toFixed(2)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${calculations.grossTotal.toFixed(2)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${calculations.grossTotal.toFixed(2)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>
    </ram:ApplicableHeaderTradeSettlement>
  </rsm:SupplyChainTradeTransaction>
</rsm:CrossIndustryInvoice>`;
}
